"use client"

import React, { useState, useEffect, useRef } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import {useNavigate } from 'react-router-dom';
import './Login.css'; // Assuming you have this CSS file
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, signInWithPhoneNumber, RecaptchaVerifier } from 'firebase/auth';
import { useAuth } from '../../Context/AuthContext';
import { appName } from '../../Constant';
import { toastFunction } from '../../Util';
import { API_V1 } from '../../Constant';
// Import your Firebase configuration from the separate file
// IMPORTANT: Adjust this path based on where you actually placed firebaseConfig.js
import firebaseConfig from '../../firebaseConfig'; 

const LoginComponent = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loadingAuth, login } = useAuth() // Use the auth context

  const [activeTab, setActiveTab] = useState('google');
  const [isLoading, setIsLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [animationClass, setAnimationClass] = useState('');
  const [error, setError] = useState(null); // State for displaying errors
  const [success, setSuccess] = useState(null); // State for displaying success messages

  // Firebase related states
  const [auth, setAuth] = useState(null);
  const recaptchaVerifierRef = useRef(null); // Use ref to store RecaptchaVerifier instance
  const confirmationResultRef = useRef(null); // To store the confirmation result for OTP verification
  const recaptchaContainerRef = useRef(null); // Ref for the recaptcha container
  const [recaptchaReady, setRecaptchaReady] = useState(false); // New state to track recaptcha readiness

  // Form data states
  const [formData, setFormData] = useState({
    phone: '',
    countryCode: '+91',
    otp: ['', '', '', '', '', '']
  });

  // Redirect already-authenticated users away from the login page
  useEffect(() => {
    if (!loadingAuth && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loadingAuth, navigate]);

  // Initialize Firebase App and Auth instance once
  useEffect(() => {
    if(!loadingAuth && isAuthenticated){
      return;
    }
    try {
      console.log("Initializing Firebase App and Auth instance...");
      // Basic check for firebaseConfig validity
      if (!firebaseConfig || !firebaseConfig.apiKey || !firebaseConfig.authDomain || !firebaseConfig.projectId) {
        throw new Error("Invalid Firebase configuration. Please check your firebaseConfig.js file.");
      }
      const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
      const firebaseAuth = getAuth(app);
      firebaseAuth.languageCode = 'en';


      setAuth(firebaseAuth);
      console.log("Firebase Auth instance set.");
    } catch (err) {
      console.error("Firebase App initialization error:", err);
      setError(err.message || "Failed to initialize Firebase App. Please check your config.");
    }
  }, []); // Runs only once on component mount

  // Setup RecaptchaVerifier when the phone tab is active and the container is in DOM
  useEffect(() => {
    let verifierInstance;

    // Condition to create a new RecaptchaVerifier:
    // 1. Auth object is available.
    // 2. Active tab is 'phone'.
    // 3. The reCAPTCHA container DOM element is available.
    // 4. No RecaptchaVerifier instance is currently stored in recaptchaVerifierRef.
    if (auth && activeTab === 'phone' && recaptchaContainerRef.current && !recaptchaVerifierRef.current) {
      console.log("reCAPTCHA container ref is available and tab is 'phone'. Attempting to create RecaptchaVerifier...");
      try {
        verifierInstance = new RecaptchaVerifier(auth, recaptchaContainerRef.current, {
          'size': 'invisible',
          'callback': (response) => {
            console.log("Recaptcha solved!", response);
          },
          'expired-callback': () => {
            console.log("Recaptcha expired. Please try again.");
            setError("Recaptcha expired. Please try sending OTP again.");
            // On expiration, clear the stored verifier and reset ready state
            if (recaptchaVerifierRef.current) {
              recaptchaVerifierRef.current.clear();
            }
            recaptchaVerifierRef.current = null;
            setRecaptchaReady(false);
            setOtpSent(false); // Go back to phone input
          }
        });

        verifierInstance.render().then((widgetId) => {
          console.log("Recaptcha rendered successfully with widget ID:", widgetId);
          recaptchaVerifierRef.current = verifierInstance; // Store the instance in ref
          setRecaptchaReady(true); // Mark recaptcha as ready
          console.log("Recaptcha is now ready.");
        }).catch((renderError) => {
          console.error("Recaptcha render error:", renderError);
          setError("Failed to render reCAPTCHA. Please check your Firebase project settings and authorized domains. Error: " + renderError.message);
          setRecaptchaReady(false);
          // CRUCIAL: If render fails, clear the instance that was just created
          // and nullify the ref to allow a fresh attempt on next render cycle.
          if (verifierInstance) {
            verifierInstance.clear(); 
          }
          recaptchaVerifierRef.current = null; 
        });
      } catch (err) {
        console.error("Recaptcha Verifier creation error:", err);
        setError(err.message || "Failed to create reCAPTCHA verifier.");
        setRecaptchaReady(false);
        recaptchaVerifierRef.current = null; // Ensure ref is null if creation fails
      }
    } else if (activeTab !== 'phone' && recaptchaVerifierRef.current) {
      // Clear reCAPTCHA when switching away from phone tab
      console.log("Switching away from phone tab. Clearing reCAPTCHA instance.");
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
      recaptchaVerifierRef.current = null;
      setRecaptchaReady(false);
    }
    
    // Cleanup function for useEffect
    // This runs when the component unmounts or when dependencies change and the effect re-runs.
    return () => {
      // Only clear if 'verifierInstance' was successfully created in this effect run
      // AND it's still the active verifier in the ref, or if the ref is managing it.
      if (verifierInstance && verifierInstance === recaptchaVerifierRef.current) {
        console.log("Running useEffect cleanup: Clearing reCAPTCHA instance on unmount/dependency change.");
        verifierInstance.clear();
        recaptchaVerifierRef.current = null; // Ensure ref is nullified on cleanup
        setRecaptchaReady(false);
      } else if (verifierInstance && verifierInstance.clear) {
        // If it's a transient instance not stored in ref, but was created
        verifierInstance.clear();
      }
    };
  }, [auth, activeTab]); // Removed recaptchaContainerRef.current from dependencies

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null); // Clear error on input change
    setSuccess(null); // Clear success on input change
  };

  // Handle OTP input with enhanced UX
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...formData.otp];
      newOtp[index] = value;
      setFormData(prev => ({
        ...prev,
        otp: newOtp
      }));
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) {
          nextInput.focus();
          nextInput.classList.add('pulse');
          setTimeout(() => nextInput.classList.remove('pulse'), 300);
        }
      }
    }
    setError(null); // Clear error on OTP input change
  };

  // Handle backspace in OTP
  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Send OTP with Firebase
  const handleSendOtp = async () => {
    if (!formData.phone) {
      setError("Please enter your phone number.");
      setAnimationClass('shake');
      setTimeout(() => setAnimationClass(''), 500);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const phoneNumber = `${formData.countryCode}${formData.phone}`;

    try {
      // Ensure auth and recaptchaVerifierRef.current are available
      if (!auth || !recaptchaVerifierRef.current) {
        throw new Error("Authentication service or reCAPTCHA not ready. Please try again.");
      }

      console.log("Attempting to verify reCAPTCHA and send OTP...");
      // Call verify on the existing instance. Firebase handles re-challenge if needed.
      await recaptchaVerifierRef.current.verify(); 

      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
      confirmationResultRef.current = confirmation; // Store confirmation result
      
      setOtpSent(true);
      setResendTimer(30);
      setSuccess("OTP sent successfully to your phone number!");
      setAnimationClass('success-pulse');
      setTimeout(() => setAnimationClass(''), 600);
      console.log("OTP sent successfully.");
    } catch (error) {
      console.error("Error sending OTP:", error);
      if (error.code === 'auth/timeout') {
        setError('The request to send an OTP has timed out. Please check your network connection and try again.');
      } else if (error.code === 'auth/captcha-check-failed') {
        setError('Phone verification failed. Please refresh the page and try again. If the problem persists, contact support.');
      } else {
        setError(error.message || "Failed to send OTP. Please try again.");
      }
      setAnimationClass('error-shake');
      setTimeout(() => setAnimationClass(''), 500);
      // On error, simply nullify the ref and set recaptchaReady to false to trigger re-initialization via useEffect
      recaptchaVerifierRef.current = null;
      setRecaptchaReady(false); 
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  // const handleResendOtp = async () => {
  //   setIsLoading(true);
  //   setError(null);
  //   setSuccess(null);

  //   const phoneNumber = `${formData.countryCode}${formData.phone}`;

  //   try {
  //     if (!auth || !recaptchaVerifierRef.current) {
  //       throw new Error("Authentication service or reCAPTCHA not ready. Please try again.");
  //     }
      
  //     console.log("Attempting to re-send OTP via existing reCAPTCHA instance...");
  //     // Simply call verify on the existing instance; Firebase handles re-challenge if needed
  //     await recaptchaVerifierRef.current.verify(); 
      
  //     const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
  //     confirmationResultRef.current = confirmation;
      
  //     setResendTimer(30);
  //     setSuccess("OTP re-sent successfully!");
  //     setAnimationClass('success-pulse');
  //     setTimeout(() => setAnimationClass(''), 600);
  //     console.log("OTP re-sent successfully.");
  //   } catch (error) {
  //     console.error("Error resending OTP:", error);
  //     if (error.code === 'auth/timeout') {
  //       setError('The request to resend an OTP has timed out. Please check your network connection and try again.');
  //     } else {
  //       setError(error.message || "Failed to resend OTP. Please try again.");
  //     }
  //     setAnimationClass('error-shake');
  //     setTimeout(() => setAnimationClass(''), 500);
  //     // On error, simply nullify the ref and set recaptchaReady to false to trigger re-initialization via useEffect
  //     recaptchaVerifierRef.current = null;
  //     setRecaptchaReady(false);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // Handle OTP verification and backend submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const otp = formData.otp.join('');
    if (otp.length !== 6) {
      setError("Please enter a 6-digit OTP.");
      setIsLoading(false);
      return;
    }

    try {
      if (!confirmationResultRef.current) {
        throw new Error("OTP verification flow not initiated. Please send OTP first.");
      }

      const userCredential = await confirmationResultRef.current.confirm(otp);
      const idToken = await userCredential.user.getIdToken();
      const phoneNumber = userCredential.user.phoneNumber;

      console.log('Firebase Phone Auth Success. Sending to backend:', { phoneNumber, idToken });

      // Send Firebase ID token and phone number to your backend
      const response = await fetch(`${API_V1}/auth/phone/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ idToken: idToken, phoneNumber: phoneNumber }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Phone login failed on the server.');
      }

      if (data.status === 'fail') {
        navigate('/register', { state: { idToken: idToken, phoneNumber: phoneNumber, isEmail: false } });
      } else {
        setSuccess("Phone login successful!");
        setAnimationClass('success-pulse');
        // navigate('/dashboard');
        setTimeout(() => setAnimationClass(''), 600);
        console.log(data);
        toastFunction("success","Welcome Back!");
        await login();
        navigate('/dashboard');
      }

    } catch (error) {
      console.error("Error verifying OTP or backend login:", error);
      if (error.code === 'auth/invalid-verification-code') {
        setError("The OTP is invalid. Please check the code and try again.");
      } else if (error.code === 'auth/code-expired') {
        setError("The OTP has expired. Please request a new one.");
      } else {
        setError(error.message || "Failed to verify OTP. Please try again.");
      }
      setAnimationClass('error-shake');
      setTimeout(() => setAnimationClass(''), 500);
    } finally {
      setIsLoading(false);
    }
  };



  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const idToken = credentialResponse.credential;
      const decodedToken = JSON.parse(atob(idToken.split('.')[1]));
      const email = decodedToken.email;
      
      const response = await fetch(`${API_V1}/auth/google/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ token: idToken, email: email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Google login failed on the server.');
      }

      if (data.status === 'fail') {
        navigate('/register', { state: { token: idToken, email: email, isEmail: true } });
      } else {
        setSuccess("Google login successful!");
        setAnimationClass('success-pulse');
        setTimeout(() => setAnimationClass(''), 600);
        console.log("Login data");
        console.log(data);
        toastFunction("success","Welcome Back!");
        await login();
        navigate('/dashboard');
      }

    } catch (error) {
      console.error("Google login error:", error);
      setError(error.message || "Google login failed. Please try again.");
      setAnimationClass('error-shake');
      setTimeout(() => setAnimationClass(''), 500);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google login failed. Please try again.");
    setAnimationClass('error-shake');
    setTimeout(() => setAnimationClass(''), 500);
  };

  // Tab switching with animation
  const handleTabSwitch = (tab) => {
    setAnimationClass('slide-out');
    setTimeout(() => {
      setActiveTab(tab);
      setOtpSent(false);
      setFormData(prev => ({ ...prev, otp: ['', '', '', '', '', ''] }));
      setError(null); // Clear errors on tab switch
      setSuccess(null); // Clear success on tab switch
      setAnimationClass('slide-in');
      setTimeout(() => setAnimationClass(''), 300);
    }, 150);
  };

  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  return (
    <div className="login-container">
      {/* Animated Background */}
      <div className="animated-bg">
        <div className="bubble bubble-1"></div>
        <div className="bubble bubble-2"></div>
        <div className="bubble bubble-3"></div>
        <div className="bubble bubble-4"></div>
        <div className="bubble bubble-5"></div>
      </div>

      {/* Left Side - Enhanced Branding */}
      <div className="login-left">
        <div className="brand-section">
          <div className="brand-logo">
            {/* <img src={logo} alt="" className="brand-logo-image" aria-hidden="true" /> */}
            <span className="logo-icon">⚡</span>
            {appName}
          </div>
          <div className="brand-tagline">
            GST Billing & Inventory Software made for Indian MSMEs
          </div>
          <div className="brand-subtitle">
            Get your invoicing organised, get paid on time, control your inventory, reduce cost, and grow business smoothly.
          </div>
        </div>

        {/* Product Preview */}
        <div className="product-preview">
            <div className="preview-window">
              <div className="window-header">
                <div className="window-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <div className="window-title">Create Invoice</div>
              </div>
              <div className="window-content">
                <div className="invoice-form">
                  <div className="form-row">
                    <div className="form-field">
                      <label>Invoice No.</label>
                      <input type="text" value="INV-001" readOnly />
                    </div>
                    <div className="form-field">
                      <label>Date</label>
                      <input type="text" value="Jan 15, 2025" readOnly />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-field full-width">
                      <label>Customer Name</label>
                      <input type="text" value="Acme Corporation" readOnly />
                    </div>
                  </div>
                  <div className="invoice-table">
                    <div className="table-header">
                      <span>Item</span>
                      <span>Qty</span>
                      <span>Rate</span>
                      <span>Amount</span>
                    </div>
                    <div className="table-row">
                      <span>Product A</span>
                      <span>2</span>
                      <span>₹500</span>
                      <span>₹1,000</span>
                    </div>
                    <div className="table-row">
                      <span>Product B</span>
                      <span>1</span>
                      <span>₹750</span>
                      <span>₹750</span>
                    </div>
                  </div>
                  <div className="invoice-total">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>₹1,750</span>
                    </div>
                    <div className="total-row">
                      <span>GST (18%):</span>
                      <span>₹315</span>
                    </div>
                    <div className="total-row final">
                      <span>Total: ₹2,065</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        {/* Feature Benefits */}
        <div className="feature-benefits">
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>No payment required • GST Approved • GSTN Integrated</span>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>Free 31-day trial • No credit card required</span>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">✓</div>
            <span>Multi-platform access • Cloud-based solution</span>
          </div>
        </div>
      </div>

      {/* Right Side - Enhanced Login Form */}
      <div className="login-right">
        <div className="mobile-brand-bar">
          {/* <img src={logo} alt="" className="mobile-brand-logo" aria-hidden="true" /> */}
          <span className="logo-icon">⚡</span>
          <span>{appName}</span>
        </div>
        <div className={`login-form-container ${animationClass}`}>
          <div className="form-header">
            <h1 className="form-title">Welcome!</h1>
            <p className="form-subtitle">Sign in to continue your journey</p>
          </div>

          {/* Display messages */}
          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}
          {success && (
            <div className="success-message" role="alert">
              {success}
            </div>
          )}

          {/* Enhanced Authentication Tabs */}
          <div className="auth-tabs">
            <div className={`tab-slider ${activeTab === 'phone' ? 'slide-right' : ''}`}></div>
            <button 
              className={`auth-tab ${activeTab === 'google' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('google')}
            >
              <span className="tab-icon">🔐</span>
              Google
            </button>
            <button 
              className={`auth-tab ${activeTab === 'phone' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('phone')}
            >
              <span className="tab-icon">📱</span>
              Phone
            </button>
          </div>

          {activeTab === 'google' ? (
            <div className="google-login-section">
              <div className="form-group">
                <p className="form-subtitle enhanced-subtitle">
                  Continue with your Google account for seamless access
                </p>
              </div>

              {isLoading ? (
                <div className="loading-container">
                  <div className="enhanced-loading">
                    <div className="loading-spinner"></div>
                    <p>Connecting...</p>
                  </div>
                </div>
              ) : (
                <div className="google-button-container">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    text="continue_with"
                    shape="rectangular"
                    size="large"
                    width="300"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="phone-login-section">
              <div className="form-group">
                <label htmlFor="phone" className="sr-only">Phone number</label>
                <div className="phone-input-container">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="form-input phone-input"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.key === 'Enter' && !otpSent && handleSendOtp()}
                  />
                </div>
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  className="login-button primary-button"
                  onClick={handleSendOtp}
                  disabled={isLoading || !formData.phone || !recaptchaReady} /* Check recaptchaReady here */
                >
                  {isLoading ? (
                    <div className="button-loading">
                      <div className="loading-spinner small"></div>
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <>
                      <span className="button-icon">📨</span>
                      Send OTP
                    </>
                  )}
                </button>
              ) : (
                <div className="otp-section">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="label-icon">🔐</span>
                      Enter Verification Code
                    </label>
                    <div className="otp-container">
                      {formData.otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          aria-label={`Digit ${index + 1} of 6`}
                          className="otp-input"
                          maxLength="1"
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="resend-section">
                    <button
                      type="button"
                      className="resend-otp"
                      onClick={handleSendOtp}
                      disabled={resendTimer > 0 || isLoading || !recaptchaReady} /* Check recaptchaReady here */
                    >
                      {resendTimer > 0 ? (
                        <>
                          <span className="timer-icon">⏱️</span>
                          Resend in {resendTimer}s
                        </>
                      ) : (
                        <>
                          <span className="resend-icon">🔄</span>
                          Resend Code
                        </>
                      )}
                    </button>
                  </div>
                  
                  <button
                    type="button"
                    className="login-button verify-button"
                    onClick={handleSubmit}
                    disabled={isLoading || formData.otp.join('').length < 6}
                  >
                    {isLoading ? (
                      <div className="button-loading">
                        <div className="loading-spinner small"></div>
                        <span>Verifying...</span>
                      </div>
                    ) : (
                      <>
                        <span className="button-icon">✅</span>
                        Verify & Continue
                      </>
                    )}
                  </button>
                </div>
              )}
              {/* This div is where reCAPTCHA will be rendered. It must be present in the DOM. */}
              <div id="recaptcha-container" ref={recaptchaContainerRef}></div>
            </div>
          )}

          {/* Enhanced Divider */}
          {activeTab === 'phone' && (
            <div className="divider">
              <div className="divider-line"></div>
              <span className="divider-text">or continue with</span>
              <div className="divider-line"></div>
            </div>
          )}

          {/* Google Login Alternative */}
          {activeTab === 'phone' && (
            <div className="google-alternative">
              {isLoading ? (
                <div className="loading-container">
                  <div className="enhanced-loading">
                    <div className="loading-spinner"></div>
                  </div>
                </div>
              ) : (
                <div className="google-button-container">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    text="continue_with"
                    shape="rectangular"
                    size="large"
                    width="300"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginComponent;
